import React from "react";
import {
  FaEnvelope,
  FaEnvelopeOpen,
  FaEye,
  FaSearch,
  FaTrash,
} from "react-icons/fa";

import {
  buildMailtoUrl,
  formatDateShort,
  getMessageDate,
  getMessageId,
  truncateText,
} from "../utils/messagesSectionUtils";

export default function MessagesTable({
  rows,
  loading,
  searchTerm,
  readFilter,
  onSearchChange,
  onReadFilterChange,
  onOpen,
  onToggleRead,
  onDelete,
}) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-4 dark:border-white/10 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Boîte de réception
            </h2>

            <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {rows.length.toLocaleString("fr-FR")} message
              {rows.length > 1 ? "s" : ""} affiché
              {rows.length > 1 ? "s" : ""}.
            </p>
          </div>

          <div className="grid gap-2 sm:grid-cols-[minmax(240px,1fr)_180px] xl:min-w-[560px]">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={searchTerm}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Rechercher nom, email ou message..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <select
              value={readFilter}
              onChange={(event) => onReadFilterChange(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200"
            >
              <option value="all">Tous</option>
              <option value="unread">Non lus</option>
              <option value="read">Lus</option>
            </select>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-5 lg:p-6">
        <div className="max-h-[72vh] overflow-auto rounded-[24px] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950/60">
          <table className="min-w-[980px] w-full text-sm">
            <thead className="sticky top-0 z-20 bg-slate-50 dark:bg-slate-900">
              <tr className="text-left">
                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Statut
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Date
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Contact
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Message
                </th>

                <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-white/10">
              {loading ? (
                [...Array(8)].map((_, index) => (
                  <tr key={index}>
                    <td colSpan={5} className="px-4 py-3">
                      <div className="h-9 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-12 text-center text-slate-500 dark:text-slate-400"
                  >
                    Aucun message ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                rows.map((message) => {
                  const messageId = getMessageId(message);
                  const isRead = Boolean(message.read_message);

                  return (
                    <tr
                      key={messageId}
                      className={[
                        "transition hover:bg-slate-50 dark:hover:bg-slate-900",
                        !isRead ? "bg-primary/[0.035] dark:bg-primary/5" : "",
                      ].join(" ")}
                    >
                      <td className="px-4 py-3">
                        <StatusBadge isRead={isRead} />
                      </td>

                      <td className="px-4 py-3 font-bold text-slate-700 dark:text-slate-300">
                        {formatDateShort(getMessageDate(message))}
                      </td>

                      <td className="px-4 py-3">
                        <div className="font-bold text-slate-900 dark:text-white">
                          {message.name || "Nom non renseigné"}
                        </div>

                        <a
                          href={buildMailtoUrl(message)}
                          className="mt-0.5 block text-xs font-semibold text-primary hover:underline"
                        >
                          {message.email || "Email non renseigné"}
                        </a>
                      </td>

                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                        {truncateText(message.message, 150)}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => onOpen(message)}
                            className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
                            title="Voir le message"
                          >
                            <FaEye />
                          </button>

                          <button
                            type="button"
                            onClick={() => onToggleRead(messageId)}
                            className={[
                              "flex h-9 w-9 items-center justify-center rounded-2xl transition",
                              isRead
                                ? "border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-white/10"
                                : "bg-primary text-white hover:opacity-90",
                            ].join(" ")}
                            title={
                              isRead
                                ? "Marquer comme non lu"
                                : "Marquer comme lu"
                            }
                          >
                            {isRead ? <FaEnvelopeOpen /> : <FaEnvelope />}
                          </button>

                          <button
                            type="button"
                            onClick={() => onDelete(messageId)}
                            className="flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-600 text-white transition hover:bg-rose-700"
                            title="Supprimer"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ isRead }) {
  return (
    <span
      className={[
        "inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1",
        isRead
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
          : "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
      ].join(" ")}
    >
      {isRead ? "Lu" : "Non lu"}
    </span>
  );
}